import React, { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';

const CONFETTI_COLORS = ['#7B61FF', '#10B981', '#F59E0B', '#3B82F6', '#EC4899', '#F0EFF4'];

const CelebrationScreen = ({
    title = "You did it!",
    subtitle = "",
    emoji = "🎉",
    nextLabel = "Continue",
    onNext,
    intensity = 'high', // 'low' | 'medium' | 'high'
}) => {
    const hasFireRef = useRef(false);

    useEffect(() => {
        if (hasFireRef.current) return;
        hasFireRef.current = true;

        const fire = () => {
            if (intensity === 'high') {
                // Big celebration — confetti cannon from both sides
                const end = Date.now() + 2500;
                const frame = () => {
                    confetti({
                        particleCount: 3,
                        angle: 60,
                        spread: 55,
                        origin: { x: 0, y: 0.7 },
                        colors: CONFETTI_COLORS,
                    });
                    confetti({
                        particleCount: 3,
                        angle: 120,
                        spread: 55,
                        origin: { x: 1, y: 0.7 },
                        colors: CONFETTI_COLORS,
                    });
                    if (Date.now() < end) requestAnimationFrame(frame);
                };
                frame();
                // Center burst after a beat
                setTimeout(() => {
                    confetti({
                        particleCount: 100,
                        spread: 100,
                        origin: { y: 0.6 },
                        colors: CONFETTI_COLORS,
                    });
                }, 400);
            } else if (intensity === 'medium') {
                confetti({
                    particleCount: 60,
                    spread: 80,
                    origin: { y: 0.65 },
                    colors: CONFETTI_COLORS,
                });
            } else {
                confetti({
                    particleCount: 30,
                    spread: 60,
                    origin: { y: 0.7 },
                    colors: CONFETTI_COLORS,
                });
            }
        };

        // Small delay so the screen renders first
        const timer = setTimeout(fire, 200);
        return () => clearTimeout(timer);
    }, [intensity]);

    return (
        <div className="animate-celebration flex flex-col items-center justify-center min-h-[60vh] text-center max-w-lg mx-auto px-4">
            {/* Emoji with glow */}
            <div className="celebration-icon w-24 h-24 rounded-full flex items-center justify-center mb-8 text-5xl"
                 style={{ background: 'radial-gradient(circle, rgba(123,97,255,0.2) 0%, transparent 70%)' }}>
                {emoji}
            </div>

            {/* Title with gradient */}
            <h2 className="celebration-title text-3xl md:text-4xl font-bold mb-4 bg-gradient-to-r from-[#F0EFF4] via-[#7B61FF] to-[#F0EFF4] bg-clip-text text-transparent bg-[length:200%] animate-shimmer">
                {title}
            </h2>

            {/* Subtitle */}
            {subtitle && (
                <p className="celebration-subtitle text-[#F0EFF4]/70 text-lg mb-10 leading-relaxed max-w-md">
                    {subtitle}
                </p>
            )}

            {/* CTA button */}
            {onNext && (
                <button
                    onClick={onNext}
                    className="celebration-cta btn-magnetic px-8 py-4 bg-[#7B61FF] hover:bg-[#6B51EF] text-[#F0EFF4] font-bold text-lg rounded-xl transition-all shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_40px_rgba(123,97,255,0.6)]"
                >
                    {nextLabel}
                </button>
            )}

            {/* CSS animations */}
            <style>{`
                @keyframes celebrationFadeUp {
                    0% { opacity: 0; transform: translateY(30px) scale(0.95); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes celebrationBounce {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.15); }
                }
                @keyframes shimmer {
                    0% { background-position: 200% center; }
                    100% { background-position: -200% center; }
                }
                .animate-celebration {
                    animation: celebrationFadeUp 0.6s ease-out;
                }
                .celebration-icon {
                    animation: celebrationBounce 0.6s ease-out 0.3s both;
                }
                .celebration-title {
                    animation: celebrationFadeUp 0.6s ease-out 0.15s both;
                }
                .animate-shimmer {
                    animation: shimmer 3s ease-in-out infinite;
                }
                .celebration-subtitle {
                    animation: celebrationFadeUp 0.6s ease-out 0.3s both;
                }
                .celebration-cta {
                    animation: celebrationFadeUp 0.6s ease-out 0.45s both;
                }
            `}</style>
        </div>
    );
};

export default CelebrationScreen;
